import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lvc-live-classes/artifacts/op54e7uq_WhatsApp%20Image%202026-04-08%20at%2013.06.26.jpeg";

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('student');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name, role);
      const user = await login(email, password);
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'teacher') navigate('/teacher');
      else navigate('/student');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="LVC Misiones" className="h-12 w-12 rounded-full object-cover" />
            <span className="font-heading text-xl font-semibold text-[#8B1538]">LVC Misiones</span>
          </Link>

          <Card className="border border-[#E5DFD3] shadow-sm">
            <CardHeader className="space-y-1">
              <CardTitle className="font-heading text-2xl text-[#1A1A1A]">Crear Cuenta</CardTitle>
              <CardDescription className="text-[#737373]">
                Completa el formulario para registrarte
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive" className="bg-[#9B2226]/10 border-[#9B2226]/20 text-[#9B2226]">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#4A4A4A]">Nombre Completo</Label>
                  <Input
                    id="name"
                    data-testid="register-name-input"
                    type="text"
                    placeholder="Tu nombre"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="border-[#E5DFD3] focus:ring-[#8B1538]/20 focus:border-[#8B1538]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#4A4A4A]">Correo Electrónico</Label>
                  <Input
                    id="email"
                    data-testid="register-email-input"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="border-[#E5DFD3] focus:ring-[#8B1538]/20 focus:border-[#8B1538]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role" className="text-[#4A4A4A]">Tipo de Cuenta</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger data-testid="register-role-select" className="border-[#E5DFD3]">
                      <SelectValue placeholder="Selecciona un tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="student">Estudiante</SelectItem>
                      <SelectItem value="teacher">Profesor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#4A4A4A]">Contraseña</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="register-password-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="border-[#E5DFD3] focus:ring-[#8B1538]/20 focus:border-[#8B1538] pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#4A4A4A]"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-[#4A4A4A]">Confirmar Contraseña</Label>
                  <Input
                    id="confirmPassword"
                    data-testid="register-confirm-password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="border-[#E5DFD3] focus:ring-[#8B1538]/20 focus:border-[#8B1538]"
                  />
                </div>
              </CardContent>
              
              <CardFooter className="flex flex-col gap-4">
                <Button 
                  type="submit"
                  data-testid="register-submit-btn"
                  className="w-full bg-[#8B1538] hover:bg-[#70102B] text-white"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Registrando...
                    </>
                  ) : (
                    'Crear Cuenta'
                  )}
                </Button>
                
                <p className="text-sm text-center text-[#737373]">
                  ¿Ya tienes una cuenta?{' '}
                  <Link to="/login" className="text-[#8B1538] hover:underline font-medium">
                    Inicia Sesión
                  </Link>
                </p>
              </CardFooter>
            </form>
          </Card>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1761435739748-879d2ecf0c70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwY29tbXVuaXR5JTIwZ2F0aGVyaW5nfGVufDB8fHx8MTc3NTY2NTYzMHww&ixlib=rb-4.1.0&q=85"
          alt="Community"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#8B1538]/90 to-[#8B1538]/70" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <img src={LOGO_URL} alt="LVC Misiones" className="h-20 w-20 rounded-full object-cover mb-8" />
          <h1 className="font-heading text-4xl lg:text-5xl font-light mb-4">
            Únete a Nuestra Comunidad
          </h1>
          <p className="text-lg text-white/80 max-w-md">
            Comienza tu formación misionera hoy y prepárate para llevar el evangelio al mundo.
          </p>
        </div>
      </div>
    </div>
  );
}
